import update from '@scoir/immutability'
import { getMonth, getYear } from 'date-fns'
import { get, head, includes, isEmpty, isUndefined, omitBy, range, size } from 'lodash'

import types from './types'

export { default as actions } from './actions'

const INITIAL_STATE = {}

const reducer = (state = INITIAL_STATE, action = {}) => {
  switch (action.type) {
    case types.FETCH_USER_SUCCESS:
      return {
        record: {
          ...action.payload,
          AbandonedScids: get(action, 'payload.AbandonedScids', {}),
        },
      }
    case types.UPDATE_PROFILE_SUCCESS:
      return update(state, {
        record: {
          Profile: {
            $set: action.payload,
          },
        },
      })
    case types.REGISTER_ABANDONED_SCID:
      return update(state, {
        record: {
          AbandonedScids: {
            $merge: action.payload,
          },
        },
      })
    default:
      return state
  }
}

export default reducer

export const selectUser = (store) => get(store, `user.record`)
export const selectUserId = (store) => get(selectUser(store), 'Id')
export const selectIsTestUser = (store) => get(store, `user.record.IsTestAppStudent`)

export const selectCollegePreferences = (store) => {
  const user = selectUser(store)
  return {
    MustHave: get(user, 'MustHave', {}),
    NiceToHave: get(user, 'NiceToHave', {}),
    DoNotHave: get(user, 'DoNotHave', {}),
  }
}

export const selectFirstName = (store) => {
  const user = selectUser(store)
  return get(user, 'FirstName')
}

export const selectAbandonedScids = (store) => {
  const user = selectUser(store)
  return { ...get(user, 'AbandonedScids', {}) }
}

export const selectProfileAvatar = (store) => {
  const user = selectUser(store)
  return get(user, 'AvatarUrl')
}

export const selectPreferredName = (store) => {
  const user = selectUser(store)
  return get(user, 'Nickname')
}

export const selectProfile = (store) => {
  const user = selectUser(store)
  return get(user, 'Profile')
}

export const selectGraduationYear = (store) => {
  const profile = selectProfile(store)
  return get(profile, 'GraduationYear')
}
export const selectAcademics = (store) => {
  const profile = selectProfile(store)
  return get(profile, 'Academics', {})
}
export const selectFerpaWaiverStatus = (store) => {
  const profile = selectProfile(store)
  return get(profile, 'FERPAWaiver.Status')
}
export const selectFerpaWaiverSignedDate = (store) => {
  const profile = selectProfile(store)
  return get(profile, 'FERPAWaiver.SignedDate')
}
export const selectFerpaWaiverEditedByName = (store) => {
  const profile = selectProfile(store)
  return get(profile, 'FERPAWaiver.EditedByName')
}
export const selectFerpaWaiverEditedDate = (store) => {
  const profile = selectProfile(store)
  return get(profile, 'FERPAWaiver.EditedDate')
}
export const selectUserRecord = (store) => selectUser(store)
export const selectId = (store) => {
  const user = selectUser(store)
  return get(user, 'Id')
}

export const selectHighSchoolId = (store) => {
  const user = selectUser(store)
  return get(user, 'HighSchool.Id')
}
export const selectSelfReportedHighSchoolDocSupport = (store) => {
  const user = selectUser(store)
  const firstHighSchool = head(get(user, 'HighSchools'))

  const highSchoolActive = get(firstHighSchool, 'Active')
  if (!highSchoolActive) {
    return false
  }

  const currentSeniorYear = get(firstHighSchool, 'CurrentSeniorYear')
  const gradYear = selectClassYear(store)

  if (gradYear < currentSeniorYear) {
    return false
  }

  const docsSupported = get(firstHighSchool, 'SupportsDocuments')
  if (!docsSupported) {
    return false
  }
  const docStartYear = get(firstHighSchool, 'DocumentStartYear')
  return gradYear >= docStartYear
}
export const selectHighSchoolDocSupport = (store) => {
  const highSchool = selectHighSchool(store)

  const currentSeniorYear = get(highSchool, 'CurrentSeniorYear')
  const gradYear = selectClassYear(store)

  if (gradYear < currentSeniorYear) {
    return false
  }

  const docsSupported = get(highSchool, 'SupportsDocuments')
  if (!docsSupported) {
    return false
  }
  const docStartYear = get(highSchool, 'DocumentStartYear')

  return gradYear >= docStartYear
}

export const selectClassYear = (store) => {
  const profile = selectProfile(store)
  return get(profile, 'GraduationYear')
}

export const selectClassYearName = (store) => {
  const profile = selectProfile(store)
  const gradYear = get(profile, 'GraduationYear')
  const seniorYear = selectCurrentSeniorYear(store)
  const names = {
    [seniorYear]: 'Senior',
    [seniorYear + 1]: 'Junior',
    [seniorYear + 2]: 'Sophomore',
    [seniorYear + 3]: 'Freshman',
  }
  if (gradYear < seniorYear) {
    return 'Alumnus'
  }
  return get(names, gradYear, 'Student')
}

export const selectIsHighSchoolStudent = (store) => {
  const userGradYear = selectClassYear(store)
  const seniorYear = selectCurrentSeniorYear(store)
  // Returns true for HS student only (freshman, sophomore, junior, senior)
  return userGradYear >= seniorYear && userGradYear <= seniorYear + 3
}

export const selectHighSchool = (store) => {
  const user = selectUser(store)
  return get(user, 'HighSchool')
}

export const selectHighSchoolStudentId = (store) => {
  const user = selectUser(store)
  return get(user, 'HighSchoolStudentId')
}

export const isLinkedStudent = (store) => !isUndefined(selectHighSchoolStudentId(store))

export const selectCurrentSeniorYear = (store) => {
  const hs = selectHighSchool(store)
  const linkedSeniorYear = get(hs, 'CurrentSeniorYear', null)
  if (linkedSeniorYear) return linkedSeniorYear

  //guess senior year
  const today = new Date()
  const currentMonth = getMonth(today)
  const currentYear = getYear(today)
  const isForNextYear = currentMonth >= 6
  return isForNextYear ? currentYear + 1 : currentYear
}

export const isActiveCurrentStudent = (store) => {
  if (!isLinkedStudent(store)) {
    return false
  }
  const profile = selectProfile(store)
  const currentSeniorYear = selectCurrentSeniorYear(store)
  const graduationYear = get(profile, 'GraduationYear', 0)
  const currentClassYears = range(currentSeniorYear, currentSeniorYear + 4)
  return includes(currentClassYears, graduationYear)
}
export const selectIsAlumni = (store) => {
  const user = selectUser(store)
  const firstHighSchool = head(get(user, 'HighSchools'))
  const currentSeniorYear = get(firstHighSchool, 'CurrentSeniorYear')
  const gradYear = selectClassYear(store)
  return gradYear < currentSeniorYear
}

export const isLinkedAndNotAlumni = (store) => {
  if (!isLinkedStudent(store)) {
    return false
  }
  const user = selectUser(store)
  const profile = selectProfile(store)
  const currentSeniorYear = get(user, 'HighSchool.CurrentSeniorYear', 0)
  const graduationYear = get(profile, 'GraduationYear', 0)
  return graduationYear >= currentSeniorYear
}

export const getHasResume = (store) => {
  const profile = selectProfile(store)
  const ec = get(profile, 'ExtraCurricular')
  const ps = get(profile, 'PersonalStatement')
  return size(ec) > 0 && !isEmpty(ps)
}

export const hasHighSchool = (store) => {
  const hs = selectHighSchool(store)
  return !isEmpty(hs)
}
export const selectPermissions = (store) => get(store, 'user.record.Permissions', [])
export const selectHasCounselorId = (store) => !!store?.user?.record?.CounselorId
export const selectPreferences = (store) => {
  const user = selectUser(store)
  return get(user, 'Preferences', {})
}
export const selectPreference = (store, pref) => {
  const preferences = selectPreferences(store)
  return get(preferences, pref, false)
}

export const selectPrinciplesYou = (store) => {
  const profile = selectProfile(store)
  return get(profile, 'PrinciplesYou')
}

const hasAny = (pref) => !isEmpty(omitBy(pref, isEmpty))
export const hasCollegePreferences = (store) => {
  const { MustHave, NiceToHave } = selectUser(store)
  return hasAny(MustHave) || hasAny(NiceToHave)
}

export const selectAppProfile = (store) => {
  const user = selectUser(store)
  return get(user, 'AppProfile', {})
}

export const selectSavedCareers = (store) => {
  const user = selectUser(store)
  return get(user, 'Profile.Careers', [])
}

export const selectRIASECScore = (store) => {
  const user = selectUser(store)
  return get(user, 'Profile.RIASECScore', {})
}

export const selectPrinciplesYouRIASECScore = (store) => {
  const user = selectUser(store)
  return get(user, 'Profile.PrinciplesYouRIASECScore', {})
}

export const selectAcceptanceLikelihoodLabels = (store) => {
  const user = selectUser(store)
  return {
    FarReach: get(user, 'HighSchool.AcceptanceLikelihoodFarReachLabel', 'Far Reach'),
    Reach: get(user, 'HighSchool.AcceptanceLikelihoodReachLabel', 'Reach'),
    Level: get(user, 'HighSchool.AcceptanceLikelihoodLevelLabel', 'Level'),
    Likely: get(user, 'HighSchool.AcceptanceLikelihoodLikelyLabel', 'Likely'),
    VeryLikely: get(user, 'HighSchool.AcceptanceLikelihoodVeryLikelyLabel', 'Very Likely'),
  }
}

export const selectSelectivityLabels = (store) => {
  const user = selectUser(store)
  const hs = get(user, 'HighSchool', {})
  return {
    Most: get(hs, 'SelectivityMostSelectiveLabel', 'Most Selective'),
    Highly: get(hs, 'SelectivityHighlySelectiveLabel', 'Highly Selective'),
    Very: get(hs, 'SelectivityVerySelectiveLabel', 'Very Selective'),
    Selective: get(hs, 'SelectivitySelectiveLabel', 'Selective'),
    Somewhat: get(hs, 'SelectivitySomewhatSelectiveLabel', 'Somewhat Selective'),
    Open: get(hs, 'SelectivityOpenAdmissionLabel', 'Open Admission'),
  }
}
